import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser, WISHLIST_LISTING_IDS } from '../../ducks/user.duck';
import { queryOwnListings } from '../ManageListingsPage/ManageListingsPage.duck';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { prepareListingImage } from '../../util/listingImageUpload';
import { ISO_LISTING_TYPE } from './shelfHelpers';

const SALE_LISTING_TYPE = 'book-listing';

export const uniqueWishlistListingIds = listingIds =>
  [...new Set(Array.isArray(listingIds) ? listingIds : [])].filter(
    listingId => typeof listingId === 'string' && listingId.length > 0
  );

const queryWishlistListingsPayloadCreator = (
  { listingIds, listingImageConfig },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const uniqueListingIds = uniqueWishlistListingIds(listingIds);
  if (uniqueListingIds.length === 0) {
    return Promise.resolve([]);
  }

  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  const params = {
    ids: uniqueListingIds,
    pub_listingType: SALE_LISTING_TYPE,
    perPage: uniqueListingIds.length,
    page: 1,
    include: ['author', 'images'],
    'fields.listing': ['title', 'price', 'deleted', 'state', 'publicData.listingType'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const listings = response.data.data.filter(listing => {
        const attributes = listing.attributes || {};
        return (
          !attributes.deleted &&
          attributes.state === 'published' &&
          attributes.publicData?.listingType === SALE_LISTING_TYPE
        );
      });
      dispatch(addMarketplaceEntities(response));
      return listings.map(listing => listing.id);
    })
    .catch(error => rejectWithValue(storableError(error)));
};

export const queryWishlistListings = createAsyncThunk(
  'WishlistPage/queryWishlistListings',
  queryWishlistListingsPayloadCreator
);

export const uploadCollectionCover = createAsyncThunk(
  'WishlistPage/uploadCollectionCover',
  async (file, { rejectWithValue, extra: sdk }) => {
    try {
      const preparedFile = await prepareListingImage(file);
      const response = await sdk.images.upload(
        { image: preparedFile },
        {
          expand: true,
          'fields.image': ['variants.scaled-medium', 'variants.scaled-medium-2x'],
          ...createImageVariantConfig('scaled-medium', 600, 1.5),
          ...createImageVariantConfig('scaled-medium-2x', 1200, 1.5),
        }
      );
      const image = response.data.data;
      const variants = image?.attributes?.variants || {};
      const variant = variants['scaled-medium'] || variants['scaled-medium-2x'];
      if (!variant?.url) {
        throw new Error('The uploaded cover did not return a usable image URL.');
      }
      return { imageUrl: variant.url, image };
    } catch (error) {
      return rejectWithValue(error?.message || 'Unable to upload this cover.');
    }
  }
);

/**
 * Looks up live ISO ("in search of") requests that could match an owned book.
 * Returns the raw listing resources; the caller decides what counts as a match
 * via `matchIsoListings` so no demand number is ever inferred here.
 */
export const queryIsoDemand = createAsyncThunk(
  'WishlistPage/queryIsoDemand',
  async ({ title }, { rejectWithValue, extra: sdk }) => {
    const keywords = typeof title === 'string' ? title.trim() : '';
    if (!keywords) {
      return [];
    }

    try {
      const response = await sdk.listings.query({
        pub_listingType: ISO_LISTING_TYPE,
        keywords,
        page: 1,
        perPage: 24,
        include: ['author'],
        'fields.listing': ['title', 'state', 'deleted', 'publicData'],
        'fields.user': ['profile.displayName'],
      });
      return response.data.data || [];
    } catch (error) {
      return rejectWithValue(storableError(error));
    }
  }
);

const wishlistPageSlice = createSlice({
  name: 'WishlistPage',
  initialState: {
    listingIds: [],
    requestedListingIds: [],
    queryInProgress: false,
    queryError: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(queryWishlistListings.pending, (state, action) => {
        state.requestedListingIds = uniqueWishlistListingIds(action.meta.arg.listingIds);
        state.queryInProgress = true;
        state.queryError = null;
      })
      .addCase(queryWishlistListings.fulfilled, (state, action) => {
        state.listingIds = action.payload;
        state.queryInProgress = false;
      })
      .addCase(queryWishlistListings.rejected, (state, action) => {
        state.listingIds = [];
        state.queryInProgress = false;
        state.queryError = action.payload;
      });
  },
});

export default wishlistPageSlice.reducer;

export const loadData = (params, search, config) => (dispatch, getState) => {
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };

  return dispatch(fetchCurrentUser(fetchCurrentUserOptions)).then(() => {
    const currentUser = getState().user.currentUser;
    const listingIds = uniqueWishlistListingIds(
      currentUser?.attributes?.profile?.privateData?.[WISHLIST_LISTING_IDS]
    );
    const wishlistLoad =
      listingIds.length === 0
        ? Promise.resolve([])
        : dispatch(
            queryWishlistListings({
              listingIds,
              listingImageConfig: config.layout.listingImage,
            })
          );
    const {
      aspectWidth = 1,
      aspectHeight = 1,
      variantPrefix = 'listing-card',
    } = config.layout.listingImage;
    const aspectRatio = aspectHeight / aspectWidth;
    return Promise.all([
      wishlistLoad,
      dispatch(
        queryOwnListings({
          page: 1,
          perPage: 42,
          include: ['images', 'currentStock'],
          'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
          ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
          ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
          'limit.images': 1,
        })
      ),
    ]);
  });
};
